import type { SegmentTrackEvent } from "@beautypie/segment"
import type { Common } from "@beautypie/segment/Common"
import dayjs from "dayjs"
import inBetween from "dayjs/plugin/isBetween"
import type { CountryType, LocaleType } from "lib/apollo/types"
import { pageContextVar } from "lib/apollo/variables/segment"
import { getLocale } from "lib/apolloClient"
import { userVar } from "lib/user"
import { getCookiePreferences } from "tracking/utils/context/cookiePreferences"
import type { MembershipType, UserType, VariantType } from "types"
import parser from "ua-parser-js"
import { getPDPUrl } from "utils/getPDPUrl"
import { siteUrl } from "utils/getSiteUrl"
import { canSendPageView } from "utils/gtm"
import { isBrowser } from "utils/isBrowser"
import { pathnameToPageType } from "utils/pathnameToPageType"
import type { PageDetails, UTMValues } from "./helpers.types"

enum StoreLocation {
  UK = "BeautyPie UK",
  US = "BeautyPie US",
}

const getStore = (
  routerLocale: string | undefined
): StoreLocation | undefined => {
  switch (routerLocale) {
    case "uk":
      return StoreLocation.UK
    case "us":
      return StoreLocation.US
    default:
      return undefined
  }
}

export const isMember = (user: UserType): boolean => {
  if (user.MembershipStartDate && user.MembershipEndDate) {
    dayjs.extend(inBetween)
    const membershipStartDate = new Date(user.MembershipStartDate)
    const membershipEndDate = new Date(user.MembershipEndDate)
    const currentDate = new Date()
    const isBetween = dayjs(currentDate).isBetween(
      membershipStartDate,
      membershipEndDate
    )
    return isBetween
  }
  return false
}

export enum ProductPriceType {
  MemberPrice = "Member Price",
  TypicalPrice = "Typical Price",
}

export const getSegmentPurchaseType = (): ProductPriceType => {
  const user = userVar()

  if (user?.isMember) return ProductPriceType.MemberPrice
  if (user?.hasMembershipInBag) return ProductPriceType.MemberPrice

  return ProductPriceType.TypicalPrice
}

export enum ProductAddRemoveLocationType {
  StickyFooter = "Sticky Footer",
  Page = "Page",
  Footer = "Footer",
  PopUp = "Pop Up",
}

let membershipLocationTypeSegment = ProductAddRemoveLocationType.Page

export const setMembershipLocationTypeSegment = (
  locationType: ProductAddRemoveLocationType
): void => {
  membershipLocationTypeSegment = locationType
}

export const getMembershipLocationTypeSegment =
  (): ProductAddRemoveLocationType => {
    const locationType = membershipLocationTypeSegment
    setMembershipLocationTypeSegment(ProductAddRemoveLocationType.Page)
    return locationType
  }
export const getUserLocale = (): LocaleType => {
  if (typeof window === "undefined") return "en-GB"
  return window.localStorage.locale
}

export const getCurrency = (locale?: LocaleType): string => {
  const location = locale || getLocale()
  switch (location) {
    case "en-US":
      return "USD"
    case "en-GB":
      return "GBP"
    default:
      return "UNKNOWN"
  }
}

export const getUrlFromWindow = (): string => {
  if (typeof window === "undefined") return ""
  return window.location.href
}

export const getPathName = (): string => {
  if (typeof window === "undefined") return ""
  return window.location.pathname
}

export const parseUTM = (): UTMValues | undefined => {
  if (isBrowser()) {
    const urlString = getUrlFromWindow()
    const url = new URL(urlString)
    const utmId = url.searchParams.get("utm_id")
    const utmSource = url.searchParams.get("utm_source")
    const utmMedium = url.searchParams.get("utm_medium")
    const utmCampaign = url.searchParams.get("utm_campaign")
    const utmTerm = url.searchParams.get("utm_term")
    const utmContent = url.searchParams.get("utm_content")
    const utmAwc = url.searchParams.get("awc")

    const utmValues: UTMValues = {
      utm_id: utmId || undefined,
      utm_source: utmSource || undefined,
      utm_medium: utmMedium || undefined,
      utm_campaign: utmCampaign || undefined,
      utm_term: utmTerm || undefined,
      utm_content: utmContent || undefined,
      awc: utmAwc || undefined,
    }
    const isEmpty = Object.values(utmValues).every(
      (field) => field === undefined
    )
    return isEmpty ? undefined : utmValues
  }
  return undefined
}

export const parseUserAgent = (): parser.IResult | undefined => {
  if (isBrowser()) {
    const ua = parser(navigator.userAgent)
    return ua
  }
  return undefined
}

export const parsePageDetails = (locale: CountryType): PageDetails => {
  let path
  let name

  const { referrer } = document
  const referrerValue = referrer === "" ? undefined : referrer

  if (isBrowser()) {
    path = window.location.pathname
    name = getPageName(path, path, locale)
  }
  const pageDetails = {
    title: document.title,
    url: window.location.href,
    referrer: referrerValue,
    path,
    search: window.location.search === "" ? undefined : window.location.search,
    name,
  }
  return pageDetails
}

export const getPageName = (
  asPath: string,
  pathname: string,
  locale: CountryType,
  shouldUpdatePrevPage = false
): string => {
  const isPageView = canSendPageView(asPath, pathname, shouldUpdatePrevPage)
  const isCmsPage = asPath.startsWith("/bp/") || asPath.startsWith("/us/bp/")
  let name: string
  // TODO: remove this check when fully migrated to the new platform from HCL
  if (isPageView || isCmsPage) {
    name = isCmsPage
      ? getCmsPageName(asPath, locale)
      : pathnameToPageType(asPath, locale)

    return name
  }
  name = "Other"
  return name
}

export const getContext = (): Common => {
  const locale = getLocale()
  const storeLocation = getStore(locale)
  const formattedLocale = getLocale()
  const utmValues = parseUTM()
  const parsedUserAgent = parseUserAgent()
  const parsedPageDetails = parsePageDetails(
    switchLocaleTypeToCountryType(locale)
  )
  const context: Common = {
    context: {
      campaign: utmValues
        ? {
            name: utmValues.utm_campaign,
            source: utmValues.utm_source,
            medium: utmValues.utm_medium,
            term: utmValues.utm_term,
            content: utmValues.utm_content,
            id: utmValues.utm_id,
            awc: utmValues.awc,
          }
        : undefined,
      device: parsedUserAgent
        ? {
            category: parsedUserAgent.device.type,
            manufacturer: parsedUserAgent.device.vendor,
            model: parsedUserAgent.device.model,
            browser: parsedUserAgent.browser.name,
            browserSize: `${window.screen.width}x${window.screen.height}`,
            browserVersion: parsedUserAgent.browser.version,
          }
        : undefined,
      ip: "",
      locale: formattedLocale,
      os: parsedUserAgent
        ? {
            name: parsedUserAgent.os.name,
            version: parsedUserAgent.os.version,
          }
        : undefined,
      page: parsedPageDetails
        ? {
            path: parsedPageDetails.path,
            referrer: parsedPageDetails.referrer,
            search: parsedPageDetails.search,
            title: parsedPageDetails.title,
            url: parsedPageDetails.url,
            name: parsedPageDetails.name,
          }
        : undefined,
      store: storeLocation,
      timezone: Intl.DateTimeFormat().resolvedOptions().timeZone,
      userAgent: navigator.userAgent,
      cookie_preferences: getCookiePreferences(),
    },
  }
  return context
}

export function notEmpty<TValue>(
  value: TValue | null | undefined
): value is TValue {
  return value !== null && value !== undefined
}

type FormatDataFunctionReturnType = {
  sku: string
  name: string
  brand: string
  variant: string
  price: number
  // eslint-disable-next-line camelcase
  members_price: number
  // eslint-disable-next-line camelcase
  typical_price: number
  url: string
}

export const formatProductDataFromVariantType = (
  variant: VariantType,
  locale: string,
  purchaseType: ProductPriceType
): FormatDataFunctionReturnType => {
  const { sku, pricing, title, brandName, subTitle } = variant
  return {
    sku,
    name: title,
    brand: brandName,
    variant: subTitle || "",
    price:
      purchaseType === ProductPriceType.MemberPrice
        ? pricing.members
        : pricing.typical,
    members_price: pricing.members,
    typical_price: pricing.typical,
    url: `${siteUrl}${getPDPUrl(variant, locale, true)}`,
  }
}

export const formatMembershipDataFromMembershipType = (
  membership: MembershipType,
  url = ""
): FormatDataFunctionReturnType => {
  const { name, price, value } = membership
  return {
    sku: value,
    name,
    brand: "Member",
    variant: "",
    price,
    typical_price: price,
    members_price: price,
    url,
  }
}

export enum NewsletterSignupLocationType {
  StickyFooter = "Sticky Footer",
  PopUp = "Pop Up",
  Footer = "Footer",
  Page = "Page",
}

export const checkAndGetContext = (
  eventData: SegmentTrackEvent
): Common["context"] => {
  function hasStepProperty(obj: any): obj is { step: number } {
    return typeof obj === "object" && obj !== null && "step" in obj
  }

  const { context } = getContext()
  const { event, properties } = eventData
  const pageContext = pageContextVar()?.page
  const isProductListViewed = event === "Product List Viewed"
  const isPageScroll = event === "Page Scroll"
  const isCartViewed = event === "Cart Viewed"
  const isCheckoutStepViewed =
    event === "Checkout Step Viewed" &&
    hasStepProperty(properties) &&
    properties?.step === 1
  const isBasketPage = context?.page?.path === "/basket"

  if (
    isProductListViewed ||
    isPageScroll ||
    (isCartViewed && !isBasketPage) ||
    isCheckoutStepViewed
  ) {
    const search = pageContext?.search || undefined
    const page = { ...pageContext, search }
    return { ...context, page }
  }

  return context
}

export const getCmsPageName = (pathName: string, locale: string): string => {
  const name = pathName.startsWith(`/${locale}/bp/`)
    ? pathName.split("/")[3]
    : pathName.split("/")[2]
  const formattedName = name
    .split(" ")
    .map((word) => word[0].toUpperCase() + word.substring(1))
    .join(" ")
  return formattedName
}

export const switchLocaleTypeToCountryType = (
  localeToSwitch: string | undefined
): CountryType => {
  if (localeToSwitch && ["us", "uk"].includes(localeToSwitch))
    return localeToSwitch as CountryType

  switch (localeToSwitch) {
    case "en-US":
      return "us"
    case "en-GB":
      return "uk"
    default:
      return "uk"
  }
}
